import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { api } from '../api';
import { STANDARD_OBJECT_TYPES } from '../objectTypes';

export function DashboardPage() {
  const [counts, setCounts] = useState<Record<string, number | null>>({});
  const [health, setHealth] = useState<{ status: string } | null>(null);

  useEffect(() => {
    void api.health().then(setHealth).catch(() => setHealth({ status: 'unreachable' }));
    for (const t of STANDARD_OBJECT_TYPES) {
      void api
        .list(t.id, { limit: 1 })
        .then((r) =>
          setCounts((c) => ({
            ...c,
            // We don't have a true count endpoint yet (M-aggregations);
            // show "has any" with the result count as a floor.
            [t.id]: r.results.length,
          })),
        )
        .catch(() => setCounts((c) => ({ ...c, [t.id]: null })));
    }
  }, []);

  return (
    <div className="p-8 max-w-6xl">
      <header className="mb-8">
        <h1 className="text-2xl font-semibold mb-1">Dashboard</h1>
        <p className="text-text-muted text-sm">
          Welcome to TheHub. Pick an object on the left to start, or use the
          tiles below.
        </p>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {STANDARD_OBJECT_TYPES.map((t) => (
          <Link
            key={t.id}
            href={`/objects/${t.id}`}
            className="card p-4 hover:border-accent transition-colors block"
          >
            <div className="text-2xl mb-2">{t.icon}</div>
            <div className="text-text-muted text-xs uppercase tracking-wider">
              {t.label_plural}
            </div>
            <div className="mt-1 text-lg font-medium">
              {counts[t.id] === undefined ? (
                <span className="text-text-dim">Loading…</span>
              ) : counts[t.id] === null ? (
                <span className="text-danger text-sm">Error</span>
              ) : counts[t.id] === 0 ? (
                <span className="text-text-dim text-sm">None yet</span>
              ) : (
                <span>{counts[t.id]}+</span>
              )}
            </div>
          </Link>
        ))}
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card p-4">
          <h2 className="text-sm font-semibold mb-3">Get started</h2>
          <ol className="space-y-2 text-sm text-text-muted">
            <li>
              <Link href="/objects/0-1/new" className="text-accent hover:underline">
                Create your first contact →
              </Link>
            </li>
            <li>
              <Link href="/objects/0-3/new" className="text-accent hover:underline">
                Add a deal →
              </Link>
            </li>
            <li className="text-text-dim">
              Set up forms, workflows, AI Copilot (coming soon)
            </li>
          </ol>
        </div>

        <div className="card p-4">
          <h2 className="text-sm font-semibold mb-3">System status</h2>
          <dl className="text-sm text-text-muted space-y-1">
            <div className="flex justify-between">
              <dt>API</dt>
              <dd
                className={
                  health?.status === 'healthy'
                    ? 'text-success'
                    : health
                      ? 'text-danger'
                      : 'text-text-dim'
                }
              >
                {health ? health.status : 'checking…'}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt>Auth</dt>
              <dd className="text-text-dim">
                {typeof window !== 'undefined' && window.location.hostname === 'localhost'
                  ? 'dev token'
                  : 'Cloudflare Access'}
              </dd>
            </div>
          </dl>
        </div>
      </section>
    </div>
  );
}
